import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAnalyticsData } from '@/hooks/useAnalyticsData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { Calendar, Users, Eye, MousePointer, Clock, Smartphone, Globe, FileText, TrendingUp, Download, UserCheck } from 'lucide-react';
import { LeadsManager } from './LeadsManager';
import { format } from 'date-fns';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe'];

export const AnalyticsDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const { data: analytics, isLoading, error } = useAnalyticsData(selectedPeriod);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">Erreur lors du chargement des données analytics</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Analytics</h1>
          <p className="text-muted-foreground">Suivez l'activité de vos visiteurs en temps réel</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={selectedPeriod === 7 ? "default" : "outline"}
            onClick={() => setSelectedPeriod(7)}
            size="sm"
          >
            7 jours
          </Button>
          <Button
            variant={selectedPeriod === 30 ? "default" : "outline"}
            onClick={() => setSelectedPeriod(30)}
            size="sm"
          >
            30 jours
          </Button>
          <Button
            variant={selectedPeriod === 90 ? "default" : "outline"}
            onClick={() => setSelectedPeriod(90)}
            size="sm"
          >
            90 jours
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sessions Totales</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalSessions?.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pages Vues</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalPageViews?.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de Rebond</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.bounceRate?.toFixed(1)}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Durée Moyenne</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(Math.round(analytics?.avgSessionDuration || 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="traffic">Trafic</TabsTrigger>
          <TabsTrigger value="behavior">Comportement</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="leads-manager">Gestion Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Traffic Over Time */}
            <Card>
              <CardHeader>
                <CardTitle>Trafic Quotidien</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics?.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'dd/MM')}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value), 'dd/MM/yyyy')}
                    />
                    <Area type="monotone" dataKey="sessions" stackId="1" stroke="#8884d8" fill="#8884d8" name="Sessions" />
                    <Area type="monotone" dataKey="pageViews" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Pages vues" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Device Types */}
            <Card>
              <CardHeader>
                <CardTitle>Types d'Appareils</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics?.deviceTypes}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ device, sessions }) => `${device}: ${sessions}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="sessions"
                    >
                      {analytics?.deviceTypes?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="traffic" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Pages */}
            <Card>
              <CardHeader>
                <CardTitle>Pages les Plus Visitées</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topPages?.map((page, index) => (
                    <div key={index} className="flex justify-between items-center p-2 rounded">
                      <span className="font-medium">{page.page}</span>
                      <Badge variant="secondary">{page.views} vues</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Sources */}
            <Card>
              <CardHeader>
                <CardTitle>Sources de Trafic</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topSources?.map((source, index) => (
                    <div key={index} className="flex justify-between items-center p-2 rounded">
                      <span className="font-medium">{source.source}</span>
                      <Badge variant="secondary">{source.sessions} sessions</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Browsers */}
          <Card>
            <CardHeader>
              <CardTitle>Navigateurs</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics?.browsers}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="browser" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sessions" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="behavior" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Métriques de Comportement</CardTitle>
              <CardDescription>Analysez comment les visiteurs interagissent avec votre site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 border rounded">
                  <MousePointer className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <div className="text-2xl font-bold">{analytics?.totalEvents?.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">Événements Totaux</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <Eye className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <div className="text-2xl font-bold">
                    {analytics?.totalSessions ? (analytics.totalPageViews / analytics.totalSessions).toFixed(1) : '0'}
                  </div>
                  <div className="text-sm text-muted-foreground">Pages/Session</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                  <div className="text-2xl font-bold">
                    {formatDuration(Math.round(analytics?.avgSessionDuration || 0))}
                  </div>
                  <div className="text-sm text-muted-foreground">Durée Moyenne</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Form Submissions Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Formulaires Soumis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {analytics?.formSubmissions}
                </div>
                <p className="text-sm text-muted-foreground">Total sur {selectedPeriod} jours</p>
              </CardContent>
            </Card>

            {/* Conversion Rate */}
            <Card>
              <CardHeader>
                <CardTitle>Taux de Conversion</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {analytics?.totalSessions && analytics?.formSubmissions 
                    ? ((analytics.formSubmissions / analytics.totalSessions) * 100).toFixed(2)
                    : '0'}%
                </div>
                <p className="text-sm text-muted-foreground">Formulaires / Sessions</p>
              </CardContent>
            </Card>

            {/* Lead Quality Score */}
            <Card>
              <CardHeader>
                <CardTitle>Score Qualité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600 mb-2">8.5/10</div>
                <p className="text-sm text-muted-foreground">Basé sur les données complètes</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Form Types */}
            <Card>
              <CardHeader>
                <CardTitle>Types de Formulaires</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topFormTypes?.map((form, index) => (
                    <div key={index} className="flex justify-between items-center p-2 rounded">
                      <span className="font-medium">{form.form_type}</span>
                      <Badge variant="secondary">{form.submissions} soumissions</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Lead Sources */}
            <Card>
              <CardHeader>
                <CardTitle>Sources des Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.leadSources?.map((source, index) => (
                    <div key={index} className="flex justify-between items-center p-2 rounded">
                      <span className="font-medium">{source.source}</span>
                      <Badge variant="secondary">{source.leads} leads</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="leads-manager" className="space-y-4">
          <LeadsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};